import { Reducer } from 'redux';
import { Action } from '../actions';
import { ActionType } from '../action-types';

// Not undoable state
type ReducerState<T extends Reducer> = ReturnType<T>;

// Undoable state
interface UndoableReducerState<T extends Reducer> {
	undoablePast: ReturnType<T>[];
	undoable: ReturnType<T>;
	undoableFuture: ReturnType<T>[];
}

type UndoCombinedState<
	TReducer extends Reducer,
	TUndoableReducer extends Reducer
> = ReducerState<TReducer> & UndoableReducerState<TUndoableReducer>;

export const undoReducer = <TReducer extends Reducer, TUndoableReducer extends Reducer>(
	reducer: TReducer,
	undoableReducer: TUndoableReducer
) => {
	const initialState: UndoCombinedState<TReducer, TUndoableReducer> = {
		undoablePast: [],
		undoable: undoableReducer(undefined, { type: undefined }),
		undoableFuture: [],
		...reducer(undefined, { type: undefined }),
	};

	return function (
		state: UndoCombinedState<TReducer, TUndoableReducer> = initialState,
		action: Action
	): UndoCombinedState<TReducer, TUndoableReducer> {
		switch (action.type) {
			case ActionType.UNDO: {
				if (state.undoablePast.length === 0) {
					return state;
				}

				return {
					...state,
					undoablePast: state.undoablePast.slice(0, state.undoablePast.length - 1),
					undoable: state.undoablePast[state.undoablePast.length - 1],
					undoableFuture: [state.undoable, ...state.undoableFuture],
				};
			}
			case ActionType.REDO: {
				if (state.undoableFuture.length === 0) {
					return state;
				}

				return {
					...state,
					undoablePast: [...state.undoablePast, state.undoable],
					undoable: state.undoableFuture[0],
					undoableFuture: state.undoableFuture.slice(1),
				};
			}
			case ActionType.SET_CURRENT_STATE: {
				return {
					...state,
					undoable: action.payload.undoable,
				};
			}
			case ActionType.RESET_STATE: {
				return initialState;
			}
			case ActionType.SET_SELECTED_DANCER:
			case ActionType.SET_SELECTED_SET_PIECE:
			case ActionType.SELECT_AUDIO_CLIP:
			case ActionType.SELECT_DANCER_PATH_NODE:
			{
				// Skip saving undo steps for these actions

				let notUndoableState = { ...state };
				delete notUndoableState.undoablePast;
				delete notUndoableState.undoable;
				delete notUndoableState.undoableFuture;
				
				const newState: UndoCombinedState<TReducer, TUndoableReducer> = {
					...reducer(notUndoableState, action),
				};
				const newUndoable = undoableReducer(state.undoable, action);
				newState.undoablePast = state.undoablePast;
				newState.undoable = newUndoable;
				newState.undoableFuture = state.undoableFuture;
				return newState;
			}
			default: {
				let notUndoableState = { ...state };
				delete notUndoableState.undoablePast;
				delete notUndoableState.undoable;
				delete notUndoableState.undoableFuture;

				const newState: UndoCombinedState<TReducer, TUndoableReducer> = {
					...reducer(notUndoableState, action),
				};
				const newUndoable = undoableReducer(state.undoable, action);
				if (state.undoable === newUndoable) {
					newState.undoablePast = state.undoablePast;
					newState.undoable = newUndoable;
					newState.undoableFuture = state.undoableFuture;
					return newState;
				} else {
					newState.undoablePast = [...state.undoablePast, state.undoable];
					newState.undoable = newUndoable;
					newState.undoableFuture = [];
					return newState;
				}
			}
		}
	};
};
